import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { authApi } from '@/services/api'
import styles from './PhoneEntry.module.css'

const COUNTRY_CODES = [
  { code: '+91', label: 'IN +91', minDigits: 10, maxDigits: 10 },
  { code: '+1', label: 'US +1', minDigits: 10, maxDigits: 10 },
  { code: '+44', label: 'UK +44', minDigits: 10, maxDigits: 10 },
  { code: '+971', label: 'AE +971', minDigits: 9, maxDigits: 9 },
  { code: '+65', label: 'SG +65', minDigits: 8, maxDigits: 8 },
  { code: '+61', label: 'AU +61', minDigits: 9, maxDigits: 9 },
]

interface PhoneEntryProps {
  onSwitchChannel?: () => void
}

export default function PhoneEntry({ onSwitchChannel }: PhoneEntryProps = {}) {
  const [countryCode, setCountryCode] = useState('+91')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const country = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0]
  const cleanedPhone = phoneNumber.replace(/\D/g, '')
  const isValidPhone = cleanedPhone.length >= country.minDigits && cleanedPhone.length <= country.maxDigits

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!isValidPhone) {
      setError(`Please enter a valid ${country.minDigits}-digit mobile number`)
      return
    }

    setLoading(true)

    try {
      const fullNumber = `${countryCode}${cleanedPhone}`
      const response = await authApi.requestOTP(fullNumber)
      sessionStorage.setItem('phone_number', response.data.phone_number)
      navigate('/verify')
    } catch (err) {
      setError('Failed to send OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Welcome to MelloMap</h1>
        <p className={styles.subtitle}>
          Enter your mobile number to get started
        </p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.inputWrapper}>
          <select
            className={styles.prefix}
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
          >
            {COUNTRY_CODES.map(c => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
          <Input
            type="tel"
            inputMode="numeric"
            placeholder="Enter mobile number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            error={error}
            maxLength={country.maxDigits}
            autoFocus
          />
        </div>

        <Button
          type="submit"
          fullWidth
          size="lg"
          loading={loading}
          disabled={!isValidPhone}
        >
          Send OTP
        </Button>

        {onSwitchChannel && (
          <button
            type="button"
            className={styles.switchLink}
            onClick={onSwitchChannel}
          >
            Not in India? Use email instead
          </button>
        )}

        <p className={styles.terms}>
          By continuing, you agree to our{' '}
          <a href="/terms" target="_blank">Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy" target="_blank">Privacy Policy</a>
        </p>
      </form>
    </div>
  )
}
